import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Calendar, BookOpen, TrendingUp } from 'lucide-react';
import { useDocsStore } from '../../stores/docsStore';
import { StatCard } from '../common/StatCard';
import { DocsToolbar } from './DocsToolbar';
import { FileTree } from './FileTree';
import { FileViewer } from './FileViewer';
import { DocsSearchResults } from './DocsSearchResults';
import { RecentFilesList } from './RecentFilesList';
import { DateList } from './DateList';
import { LogContent } from './LogContent';

export function DocsPage() {
  const {
    viewMode, searchQuery,
    dates, stats, fetchDates, fetchStats, setSelectedDate,
    navigateToPath, fetchRoots, fetchRecentFiles,
  } = useDocsStore();

  const [searchParams] = useSearchParams();

  // Load roots on mount
  useEffect(() => {
    fetchRoots();
  }, [fetchRoots]);

  // Handle ?path= query param for deep linking
  useEffect(() => {
    const pathParam = searchParams.get('path');
    if (pathParam) {
      navigateToPath(pathParam);
    }
  }, [searchParams, navigateToPath]);

  // Fetch recent files when in today mode
  useEffect(() => {
    if (viewMode === 'today') {
      fetchRecentFiles();
    }
  }, [viewMode, fetchRecentFiles]);

  // Fetch legacy data when in legacy mode
  useEffect(() => {
    if (viewMode === 'legacy') {
      fetchDates();
      fetchStats();
    }
  }, [viewMode, fetchDates, fetchStats]);

  // Auto-select most recent date in legacy mode
  useEffect(() => {
    if (viewMode === 'legacy' && dates.length > 0 && !useDocsStore.getState().selectedDate) {
      setSelectedDate(dates[0].date);
    }
  }, [viewMode, dates, setSelectedDate]);

  const showSearch = searchQuery.length >= 2;

  return (
    <div className="h-[calc(100vh-3.5rem)] flex flex-col">
      {/* Toolbar */}
      <DocsToolbar />

      {/* Stats bar (legacy mode only) */}
      {viewMode === 'legacy' && (
        <div className="flex-shrink-0 p-4 border-b border-border-subtle">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <StatCard label="Total Days" value={stats?.totalDays ?? 0} icon={<Calendar />} />
            <StatCard label="This Month" value={stats?.daysThisMonth ?? 0} icon={<BookOpen />} />
            <StatCard label="Avg Sessions/Day" value={stats?.avgEntriesPerDay ?? 0} icon={<TrendingUp />} />
          </div>
        </div>
      )}

      {/* Split panel */}
      <div className="flex flex-col md:flex-row flex-1 min-h-0">
        {/* Left sidebar */}
        <div className="w-full md:w-72 flex-shrink-0 border-b md:border-b-0 md:border-r border-border-subtle overflow-y-auto max-h-48 md:max-h-none">
          {showSearch ? (
            <DocsSearchResults />
          ) : viewMode === 'today' ? (
            <RecentFilesList />
          ) : viewMode === 'tree' ? (
            <FileTree />
          ) : (
            <DateList />
          )}
        </div>

        {/* Right content */}
        <div className="flex-1 min-w-0">
          {viewMode === 'legacy' ? <LogContent /> : <FileViewer />}
        </div>
      </div>
    </div>
  );
}

export default DocsPage;
